import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTabs } from "../context/TabsContext"

const STATUS_STEPS = [
  { id: "active",    icon: "hammer",         labelKey: "projects.lifecycle.active" },
  { id: "reception", icon: "handshake",      labelKey: "projects.lifecycle.reception" },
  { id: "completed", icon: "flag-checkered", labelKey: "projects.lifecycle.completed" },
]

const statusIndex = status => {
  switch (status) {
    case "active":    return 0
    case "on_hold":   return 0
    case "reception": return 1
    case "completed": return 2
    default:          return -1
  }
}

const Step = ({ icon, label, done, current, muted }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ring-1 whitespace-nowrap ${
      current
        ? "bg-indigo-50 text-indigo-800 ring-indigo-200 font-semibold"
        : done
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : muted
            ? "bg-slate-50 text-slate-400 ring-slate-200"
            : "bg-slate-50 text-slate-500 ring-slate-200"
    }`}
  >
    <FontAwesomeIcon icon={done ? "check" : icon} className="w-3 h-3" />
    {label}
  </span>
)

const Chain = ({ steps, onAct }) => (
  <div className="px-7 py-2.5 border-b border-slate-100 bg-white">
    <div className="flex items-center gap-2 text-[11.5px] overflow-x-auto no-scrollbar">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <Step {...step} />
          {index < steps.length - 1 && (
            <span className={`w-6 h-px shrink-0 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`} />
          )}
        </React.Fragment>
      ))}

      {/* The action itself lives in the header, where there is exactly one of
          them. This is the way in to the detail, not a second command. */}
      {onAct && (
        <button
          type="button"
          onClick={onAct}
          className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-900"
        >
          {I18n.t("projects.lifecycle.view_milestones")}
          <FontAwesomeIcon icon="arrow-right" className="w-3 h-3" />
        </button>
      )}
    </div>
  </div>
)

const LifecycleStrip = ({ project, standard, downPayment, acceptance, dgd, retention }) => {
  const { goTo } = useTabs()
  if (!project) return null
  if (["cancelled", "draft"].includes(project.status)) return null

  if (standard) {
    const current = statusIndex(project.status)
    if (current < 0) return null

    return (
      <Chain
        steps={STATUS_STEPS.map((step, index) => ({
          id:      step.id,
          icon:    step.icon,
          label:   I18n.t(step.labelKey),
          done:    index < current,
          current: index === current,
        }))}
      />
    )
  }

  const hasDownPayment = !!downPayment?.exists
  const accepted       = !!acceptance?.exists
  const hasDgd         = !!dgd?.exists
  const invoicing      = project.status === "active" || accepted
  const retentionOpen  = !!retention?.enabled && !retention?.released

  const steps = [
    { id: "down_payment", icon: "hand-holding-dollar", label: I18n.t("projects.lifecycle.steps.down_payment"), done: hasDownPayment, current: !hasDownPayment && !accepted },
    { id: "situations",   icon: "file-invoice",        label: I18n.t("projects.lifecycle.steps.situations"), done: accepted,      current: hasDownPayment && !accepted, muted: !invoicing },
    { id: "acceptance",   icon: "stamp",               label: I18n.t("projects.lifecycle.steps.acceptance"), done: accepted,       current: false },
    { id: "dgd",          icon: "flag-checkered",      label: I18n.t("projects.lifecycle.steps.dgd"), done: hasDgd,         current: accepted && !hasDgd, muted: !accepted },
  ]

  if (retentionOpen) {
    steps.splice(3, 0, {
      id:    "retention",
      icon:  "lock",
      label: I18n.t("projects.lifecycle.steps.retention", { pct: retention.percentage || 5 }),
      done:  false,
      muted: !accepted,
    })
  }

  return <Chain steps={steps} onAct={() => goTo("milestones")} />
}

export default LifecycleStrip
